/**
 * Company Integrations Tab
 * Credenciais que o MEV-Med usa para chamar sistemas externos em nome desta empresa
 * (Sankhya, Senior HCM, Dimep Kairos, Ponto Icarus). Preenchimento de uma vez só — os campos
 * secretos são write-only: nunca voltam em claro do backend, só um booleano "preenchido".
 *
 * Layout em linha (estilo "conexões de app"): cada fornecedor é uma linha com marca, status e
 * uma ação — editar abre o formulário num diálogo, em vez de deixar os 4 formulários sempre
 * abertos na tela.
 *
 * **Desligar e excluir são ações diferentes, e é de propósito.** O interruptor da linha só vira
 * `active`, que é o que o job noturno consulta: a integração para de rodar e a credencial fica
 * guardada. Excluir apaga o segredo, e por isso pede confirmação — como os campos secretos são
 * write-only, uma exclusão só se desfaz com alguém redigitando a senha do fornecedor. Os dois
 * caminhos já estiveram colapsados num clique só; `CompanyIntegrationsTab.desligar.test.tsx`
 * existe para impedir que voltem a ser.
 */

import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Stack,
  Switch,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Cable as CableIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ProfileSection } from '../../../../components/common';
import { useAcao } from '../../../../authorization/useVeredito';
import { integrationCredentialsService } from '../../../../services/integrationCredentialsService';
import {
  INTEGRATION_PROVIDER_DEFINITIONS,
  IntegrationCredentialSummary,
  IntegrationProvider,
} from '../../../../types/integrationCredentials';

interface CompanyIntegrationsTabProps {
  companyId: string;
}

/** Cor de identificação de cada fornecedor na marca da linha — não é status, só rótulo visual. */
const PROVIDER_MARK_COLOR: Record<IntegrationProvider, string> = {
  sankhya: 'info.main',
  senior_hcm: 'secondary.main',
  dimep_kairos: 'primary.main',
  ponto_icarus: '#1E293B',
};

type FormFields = Record<string, string>;

function camposVazios(provider: IntegrationProvider): FormFields {
  const def = INTEGRATION_PROVIDER_DEFINITIONS.find(d => d.provider === provider);
  const vazio: FormFields = {};
  def?.fields.forEach(f => { vazio[f.key] = ''; });
  return vazio;
}

const CompanyIntegrationsTab: React.FC<CompanyIntegrationsTabProps> = ({ companyId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const acao = useAcao('company.manage-integrations', 'Gerenciar integrações');

  const [summaries, setSummaries] = useState<Record<string, IntegrationCredentialSummary | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editando, setEditando] = useState<IntegrationProvider | null>(null);
  const [label, setLabel] = useState('');
  const [fields, setFields] = useState<FormFields>({});
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState<IntegrationProvider | null>(null);
  const [alternando, setAlternando] = useState<IntegrationProvider | null>(null);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState<IntegrationProvider | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const list = await integrationCredentialsService.list(companyId);
      const porProvider: Record<string, IntegrationCredentialSummary> = {};
      for (const item of list) porProvider[item.provider] = item;
      setSummaries(porProvider);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar integrações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  function abrirEdicao(provider: IntegrationProvider): void {
    const def = INTEGRATION_PROVIDER_DEFINITIONS.find(d => d.provider === provider);
    setLabel(summaries[provider]?.label ?? def?.label ?? '');
    setFields(camposVazios(provider));
    setEditando(provider);
  }

  function fecharEdicao(): void {
    if (salvando) return;
    setEditando(null);
  }

  async function handleSalvar(): Promise<void> {
    if (!editando) return;
    setSalvando(true);
    try {
      const saved = await integrationCredentialsService.save(companyId, editando, { label, fields });
      setSummaries(prev => ({ ...prev, [editando]: saved }));
      enqueueSnackbar('Credencial salva com sucesso', { variant: 'success' });
      setEditando(null);
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao salvar credencial', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  }

  /**
   * O interruptor liga e desliga, e nunca apaga.
   *
   * Desligar pausa a sincronização (o job noturno só busca credencial com `active`) e mantém o
   * segredo guardado. Excluir é a outra ação, com confirmação — porque o campo secreto é
   * write-only: uma vez apagado, só volta se alguém tiver a senha em mãos para redigitar.
   */
  async function handleAlternarAtiva(provider: IntegrationProvider, ativa: boolean): Promise<void> {
    setAlternando(provider);
    try {
      const atualizada = await integrationCredentialsService.setActive(companyId, provider, ativa);
      setSummaries(prev => ({ ...prev, [provider]: atualizada }));
      enqueueSnackbar(ativa ? 'Integração ligada' : 'Integração desligada', { variant: 'success' });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao alterar a integração', { variant: 'error' });
    } finally {
      setAlternando(null);
    }
  }

  async function handleRemover(provider: IntegrationProvider): Promise<void> {
    setRemovendo(provider);
    try {
      await integrationCredentialsService.remove(companyId, provider);
      setSummaries(prev => ({ ...prev, [provider]: undefined }));
      enqueueSnackbar('Credencial removida', { variant: 'success' });
      setConfirmandoRemocao(null);
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao remover credencial', { variant: 'error' });
    } finally {
      setRemovendo(null);
    }
  }

  const defEditando = editando ? INTEGRATION_PROVIDER_DEFINITIONS.find(d => d.provider === editando) : undefined;
  const summaryEditando = editando ? summaries[editando] : undefined;
  const defConfirmando = confirmandoRemocao
    ? INTEGRATION_PROVIDER_DEFINITIONS.find(d => d.provider === confirmandoRemocao)
    : undefined;

  return (
    <ProfileSection title="Integrações" icon={<CableIcon />}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Conecte sistemas externos para trocar dados com esta empresa
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
          {INTEGRATION_PROVIDER_DEFINITIONS.map(def => {
            const summary = summaries[def.provider];
            // Ter credencial salva e estar ligada são coisas diferentes: sem essa separação, uma
            // integração desligada voltaria a mostrar "Conectar" e não haveria caminho de volta
            // para religá-la sem redigitar o segredo.
            const temCredencial = summary !== undefined;
            const ligada = Boolean(summary?.active);
            const alternandoEsta = alternando === def.provider;

            return (
              <Stack
                key={def.provider}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ py: 2 }}
              >
                <Avatar sx={{ bgcolor: PROVIDER_MARK_COLOR[def.provider], width: 40, height: 40, fontSize: 14, fontWeight: 700 }}>
                  {def.label.slice(0, 2).toUpperCase()}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {def.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {temCredencial ? 'Credencial salva' : 'Nenhuma credencial salva'}
                  </Typography>
                </Box>

                {temCredencial ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ color: ligada ? 'success.main' : 'text.disabled' }}
                    >
                      {ligada ? 'Conectado' : 'Desligado'}
                    </Typography>
                    <Switch
                      size="small"
                      color="success"
                      checked={ligada}
                      disabled={acao.disabled || alternandoEsta}
                      // `role` explícito porque `slotProps.input` substitui o `inputProps` que o
                      // MUI monta sozinho, e é lá que ele põe o `role="switch"`. Sem isto o
                      // controle volta a se anunciar como caixa de seleção para o leitor de tela.
                      slotProps={{
                        input: { role: 'switch', 'aria-label': `Ligar ou desligar ${def.label}` },
                      }}
                      onChange={(_e, marcado) => void handleAlternarAtiva(def.provider, marcado)}
                    />
                    <Button size="small" onClick={() => abrirEdicao(def.provider)} disabled={acao.disabled}>
                      Editar
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => abrirEdicao(def.provider)}
                    disabled={acao.disabled}
                  >
                    Conectar
                  </Button>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      <Dialog open={editando !== null} onClose={fecharEdicao} maxWidth="xs" fullWidth>
        {defEditando && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: PROVIDER_MARK_COLOR[defEditando.provider], width: 32, height: 32, fontSize: 13, fontWeight: 700 }}>
                {defEditando.label.slice(0, 2).toUpperCase()}
              </Avatar>
              {defEditando.label}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
              {/*
                Salvar não religa de propósito: retomar sozinha uma sincronização que alguém
                pausou é efeito colateral, não conveniência. Mas sem este aviso quem veio
                justamente corrigir uma senha errada sairia daqui achando que resolveu, e a
                integração seguiria parada em silêncio até alguém reparar no interruptor.
              */}
              {summaryEditando && !summaryEditando.active && (
                <Alert severity="info">
                  Esta integração está desligada. Salvar atualiza a credencial e a mantém
                  desligada — use o interruptor da lista para voltar a sincronizar.
                </Alert>
              )}
              <TextField
                label="Nome da conexão"
                size="small"
                value={label}
                onChange={e => setLabel(e.target.value)}
                fullWidth
                autoFocus
              />
              {defEditando.fields.map(campo => {
                const jaPreenchido = summaryEditando?.filledFields?.[campo.key] ?? false;
                return (
                  <TextField
                    key={campo.key}
                    label={campo.label}
                    size="small"
                    type={campo.secret ? 'password' : 'text'}
                    value={fields[campo.key] ?? ''}
                    onChange={e => setFields(prev => ({ ...prev, [campo.key]: e.target.value }))}
                    placeholder={jaPreenchido ? '•••••••• (deixe em branco para manter)' : ''}
                    autoComplete={campo.secret ? 'new-password' : 'off'}
                    fullWidth
                  />
                );
              })}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {summaryEditando && (
                <Button
                  color="error"
                  disabled={salvando}
                  sx={{ mr: 'auto' }}
                  onClick={() => { setEditando(null); setConfirmandoRemocao(defEditando.provider); }}
                >
                  Remover
                </Button>
              )}
              <Button onClick={fecharEdicao} disabled={salvando}>Cancelar</Button>
              <Button variant="contained" onClick={() => void handleSalvar()} disabled={salvando}>
                {salvando ? 'Salvando…' : 'Salvar'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/*
        Confirmação de verdade, e não um "tem certeza?" de praxe: o campo secreto é write-only e
        não volta do banco. Quem confirma sem ler perde a senha do fornecedor, e religar a
        integração depende de alguém ter esse segredo em mãos outra vez.
      */}
      <Dialog
        open={confirmandoRemocao !== null}
        onClose={() => { if (removendo === null) setConfirmandoRemocao(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover a credencial?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A senha de {defConfirmando?.label ?? 'este fornecedor'} será apagada e não pode ser
            recuperada — religar a integração exigirá digitá-la de novo.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Para apenas pausar a sincronização, feche aqui e use o interruptor da linha: ele
            desliga a integração e mantém a credencial guardada.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmandoRemocao(null)} disabled={removendo !== null}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={removendo !== null}
            onClick={() => { if (confirmandoRemocao) void handleRemover(confirmandoRemocao); }}
          >
            {removendo !== null ? 'Removendo…' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfileSection>
  );
};

export default CompanyIntegrationsTab;
